import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  Assessment,
  AssessmentApplication,
  AssessmentApplicationFilters,
  AssessmentApplicationType,
  AssessmentAssignPayload,
  AssessmentAssignResponse,
  AssessmentAssignmentFilters,
  AssessmentDetailFilters,
  AssessmentFilters,
  AssessmentMarkViewedPayload,
  AssessmentMarkViewedResponse,
  AssessmentUnreadCountResponse,
  AnswerOption,
  Attempt,
  AttemptCorrectionReview,
  AttemptCorrectionReviewViewedResponse,
  AttemptBulkResponseCreatePayload,
  AttemptBulkResponseSubmission,
  AttemptAudioPlaybackDownloadResponse,
  AttemptAudioPlaybackStartResponse,
  AttemptFilters,
  AttemptResponseSubmission,
  AttemptNextResponse,
  AttemptResponseCreatePayload,
  AttemptSubmitResponse,
  PaginatedResponse,
  Question,
  QuestionFilters,
  RecalculateAttemptResponse,
  Response,
  ResponseFilters,
  ResponseManualCorrectionPayload,
  StudentAssessmentAssignment,
} from "~/types/admin/assessment";
import { buildAttemptAudioPlaybackDownloadResponse } from "~/repository/modules/assessmentAudioPlayback";
import {
  buildAttemptBulkResponseSubmissionPayload,
  buildAttemptResponseSubmissionPayload,
} from "~/utils/assessmentResponseSubmission";
import type {
  AssessmentAuthoringCreatePayload,
  AssessmentAuthoringDocument,
  AssessmentAuthoringUpdatePayload,
  AssessmentGenerationJob,
  AssessmentGenerationJobCreatePayload,
  AssessmentGenerationOptions,
  AssessmentResultsFilters,
  AssessmentResultsResponse,
  StudentActivity,
  StudentActivityFilters,
} from "~/types/assessmentAuthoring";

class AssessmentModule extends HttpFactory {
  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance, "teacher");
  }

  async getAssessments(
    filters?: AssessmentFilters,
  ): Promise<PaginatedResponse<Assessment>> {
    return await this.get(ENDPOINTS.ASSESSMENT.ASSESSMENTS, filters || {});
  }

  async getAssessmentAssignments(
    filters?: AssessmentAssignmentFilters,
  ): Promise<PaginatedResponse<StudentAssessmentAssignment>> {
    return await this.get(ENDPOINTS.ASSESSMENT.ASSIGNMENTS, filters || {});
  }

  async getAssessmentAssignmentsUnreadCount(): Promise<AssessmentUnreadCountResponse> {
    return await this.get(ENDPOINTS.ASSESSMENT.ASSIGNMENTS_UNREAD_COUNT);
  }

  async markAssessmentAssignmentsViewed(
    assignmentIds: number[],
  ): Promise<AssessmentMarkViewedResponse> {
    const payload: AssessmentMarkViewedPayload = {
      assignment_ids: assignmentIds,
    };
    return await this.post(
      ENDPOINTS.ASSESSMENT.ASSIGNMENTS_MARK_VIEWED,
      payload,
    );
  }

  async getAssessmentApplications(
    filters?: AssessmentApplicationFilters,
  ): Promise<PaginatedResponse<AssessmentApplication>> {
    return await this.get(ENDPOINTS.ASSESSMENT.APPLICATIONS, filters || {});
  }

  async getStudentActivities(
    studentId: number,
    filters?: StudentActivityFilters,
  ): Promise<PaginatedResponse<StudentActivity>> {
    return await this.get(
      ENDPOINTS.ASSESSMENT.STUDENT_ACTIVITIES.replace(
        "{studentId}",
        String(studentId),
      ),
      filters || {},
    );
  }

  async getAssessmentApplication(
    applicationType: AssessmentApplicationType,
    applicationId: number,
  ): Promise<AssessmentApplication> {
    return await this.get(
      `${ENDPOINTS.ASSESSMENT.APPLICATIONS}${applicationType}/${applicationId}/`,
    );
  }

  async getAssessment(
    id: number,
    filters?: AssessmentDetailFilters,
  ): Promise<Assessment> {
    return await this.get(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/`,
      filters || {},
    );
  }

  async createTeacherAssessment(
    payload: AssessmentAuthoringCreatePayload,
  ): Promise<Assessment> {
    return await this.post(ENDPOINTS.ASSESSMENT.ASSESSMENTS, payload);
  }

  async getAssessmentAuthoring(
    id: number,
  ): Promise<AssessmentAuthoringDocument> {
    return await this.get(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/authoring/`,
    );
  }

  async updateAssessmentAuthoring(
    id: number,
    payload: AssessmentAuthoringUpdatePayload | FormData,
  ): Promise<AssessmentAuthoringDocument> {
    return await this.put(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/authoring/`,
      payload,
    );
  }

  async duplicateTeacherAssessment(
    id: number,
  ): Promise<AssessmentAuthoringDocument> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/duplicate/`,
      {},
    );
  }

  async archiveTeacherAssessment(
    id: number,
  ): Promise<AssessmentAuthoringDocument> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/archive/`,
      {},
    );
  }

  async restoreTeacherAssessment(
    id: number,
  ): Promise<AssessmentAuthoringDocument> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/restore/`,
      {},
    );
  }

  async permanentlyDeleteTeacherAssessment(id: number): Promise<void> {
    await this.post<void>(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/permanent-delete/`,
      { confirm_history_loss: true },
    );
  }

  async createAssessmentGenerationJob(
    payload: AssessmentGenerationJobCreatePayload,
  ): Promise<AssessmentGenerationJob> {
    return await this.post(ENDPOINTS.ASSESSMENT.GENERATION_JOBS, payload);
  }

  async getAssessmentGenerationOptions(): Promise<AssessmentGenerationOptions> {
    return await this.get(ENDPOINTS.ASSESSMENT.GENERATION_OPTIONS);
  }

  async getAssessmentGenerationJob(
    id: number,
  ): Promise<AssessmentGenerationJob> {
    return await this.get(`${ENDPOINTS.ASSESSMENT.GENERATION_JOBS}${id}/`);
  }

  async getAssessmentResults(
    id: number,
    filters?: AssessmentResultsFilters,
  ): Promise<AssessmentResultsResponse> {
    return await this.get(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/results/`,
      filters || {},
    );
  }

  async getQuestions(
    filters?: QuestionFilters,
  ): Promise<PaginatedResponse<Question>> {
    return await this.get(ENDPOINTS.ASSESSMENT.QUESTIONS, filters || {});
  }

  async getAnswerOptions(
    questionId?: number,
  ): Promise<PaginatedResponse<AnswerOption>> {
    return await this.get(
      ENDPOINTS.ASSESSMENT.ANSWER_OPTIONS,
      questionId ? { question: questionId } : {},
    );
  }

  async getAttempts(
    filters?: AttemptFilters,
  ): Promise<PaginatedResponse<Attempt>> {
    return await this.get(ENDPOINTS.ASSESSMENT.ATTEMPTS, filters || {});
  }

  async getAttempt(id: number): Promise<Attempt> {
    return await this.get(`${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/`);
  }

  async getAttemptGrading(id: number): Promise<Record<string, unknown>> {
    return await this.get(`${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/grading/`);
  }

  async completeAttemptForGrading(id: number): Promise<Attempt> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/complete-for-grading/`,
      {},
    );
  }

  async recalculateAttempt(id: number): Promise<RecalculateAttemptResponse> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/recalculate/`,
      {},
    );
  }

  async releaseCorrection(id: number): Promise<Attempt> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/release-correction/`,
      {},
    );
  }

  async releaseNextAttempt(id: number): Promise<Attempt> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/release-next-attempt/`,
      {},
    );
  }

  async getCorrectionReview(id: number): Promise<AttemptCorrectionReview> {
    return await this.get(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/correction-review/`,
    );
  }

  async markCorrectionReviewViewed(
    id: number,
  ): Promise<AttemptCorrectionReviewViewedResponse> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/correction-review/viewed/`,
      {},
    );
  }

  async simulateAssessment(id: number): Promise<Attempt> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/simulate/`,
      {},
    );
  }

  async createAttempt(
    assessmentId: number,
    assignmentId?: number | null,
  ): Promise<Attempt> {
    const payload =
      assignmentId !== undefined && assignmentId !== null
        ? { assignment_id: assignmentId }
        : {};
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${assessmentId}/attempts/`,
      payload,
    );
  }

  async getNextAttemptQuestion(id: number): Promise<AttemptNextResponse> {
    return await this.get(`${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/next/`);
  }

  async submitAttemptResponse(
    id: number,
    data: AttemptResponseCreatePayload,
  ): Promise<AttemptResponseSubmission> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/responses/`,
      buildAttemptResponseSubmissionPayload(data),
    );
  }

  async submitAttemptResponsesBulk(
    id: number,
    data: AttemptBulkResponseCreatePayload,
  ): Promise<AttemptBulkResponseSubmission> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/responses/bulk/`,
      buildAttemptBulkResponseSubmissionPayload(data),
    );
  }

  async startAttemptAudioPlayback(
    id: number,
    assetId: number,
  ): Promise<AttemptAudioPlaybackStartResponse> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/audio-playbacks/${assetId}/start/`,
      {},
    );
  }

  async downloadAttemptAudioPlayback(
    audioUrl: string,
  ): Promise<AttemptAudioPlaybackDownloadResponse> {
    const response = await this.getResponse<Blob>(
      audioUrl,
      {},
      { responseType: "blob" },
    );
    return buildAttemptAudioPlaybackDownloadResponse(response);
  }

  async downloadAudioSample(): Promise<Blob> {
    return await this.get(
      ENDPOINTS.ASSESSMENT.AUDIO_SAMPLE,
      {},
      { responseType: "blob" },
    );
  }

  async submitAttempt(id: number): Promise<AttemptSubmitResponse> {
    return await this.post(`${ENDPOINTS.ASSESSMENT.ATTEMPTS}${id}/submit/`, {});
  }

  async getResponses(
    filters?: ResponseFilters,
  ): Promise<PaginatedResponse<Response>> {
    return await this.get(ENDPOINTS.ASSESSMENT.RESPONSES, filters || {});
  }

  async updateResponse(
    id: number,
    data: ResponseManualCorrectionPayload,
  ): Promise<Response> {
    return await this.patch(`${ENDPOINTS.ASSESSMENT.RESPONSES}${id}/`, data);
  }

  async assignAssessment(
    id: number,
    data: AssessmentAssignPayload,
  ): Promise<AssessmentAssignResponse> {
    return await this.post(
      `${ENDPOINTS.ASSESSMENT.ASSESSMENTS}${id}/assign/`,
      data,
    );
  }
}

export default AssessmentModule;
